import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';

type EXPERIMENT_OPTIONS = 'never' | 'always' | 'hover';

const useIsExperimentEligible = () => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);

	/* Some URL patterns where Issue view is rendered either in a modal or full page or in a sidebar
	The common pattern is presence of issue key in the URL pathname or search params
	1. /browse/TEST-1
	2. /issues/TEST-1
	3. /jira/software/c/projects/TEST/issues/TEST-1
	4. /jira/software/c/projects/TEST/boards/1?selectedIssue=TEST-1
	5. /jira/software/c/projects/TEST/boards/1/backlog?selectedIssue=TEST-1
	6. /jira/software/c/projects/TEST/list?selectedIssue=TEST-1
	*/
	const ISSUE_KEY_REGEX = /[A-Z][A-Z0-9]+-\d+/;
	let isFieldRenderedInIssueView = false;
	try {
		isFieldRenderedInIssueView =
			// eslint-disable-next-line no-restricted-globals
			ISSUE_KEY_REGEX.test(location.search) || ISSUE_KEY_REGEX.test(location.pathname);
	} catch (error) {
		// If regex test fails for some reason, default to false
		isFieldRenderedInIssueView = false;
	}

	return (
		projectType === SOFTWARE_PROJECT &&
		isFieldRenderedInIssueView &&
		fg('bespoke-empty-field-text-gate')
	);
};

export const useBespokeEmptyFieldTextExperiment = () => {
	const isExperimentEligible = useIsExperimentEligible();
	const isVisibleAlways =
		isExperimentEligible &&
		expValEquals<EXPERIMENT_OPTIONS>(
			'bespoke_empty_field_text_experiment',
			'showBespokeFieldText',
			'always',
			'never',
		);
	const isVisibleOnHover =
		isExperimentEligible &&
		expValEquals<EXPERIMENT_OPTIONS>(
			'bespoke_empty_field_text_experiment',
			'showBespokeFieldText',
			'hover',
			'never',
		);

	return {
		bespokeEmptyFieldTextExperimentVariant: isExperimentEligible
			? expVal<EXPERIMENT_OPTIONS>(
					'bespoke_empty_field_text_experiment',
					'showBespokeFieldText',
					'never',
				)
			: null,
		isExperimentEnabled: isVisibleAlways || isVisibleOnHover,
		isVisibleAlways,
		isVisibleOnHover,
	};
};
